import { Component, OnInit, Input, Output, EventEmitter, ViewChild, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { UserConfirmService } from 'src/app/services/user-confirm.service';
import { DentalDisabilityMemoService, DentalDisabilityFormData, DentalDisabilityFormDataOptimized } from './dental-disability-memo.service';

export interface DisabilityMemoData {
  patientNo: string;
  patientName: string;
  patientCId?: string;
  disabilityCategories?: string[]; // New categories (8-class)
  oldDisabilityCategories?: string[]; // Old categories (16-class)
  severityLevels?: { [key: string]: string }; // key: oldCategoryCode, value: severity level
  memo?: string;
  treatmentItems?: string[];
  specialRequirements?: string;
  leftImageUrl?: string;
  rightImageUrl?: string;
  createdDate?: string;
  modifiedDate?: string;
  [key: string]: any;
}

// 新制類別（8類）
interface NewDisabilityCategory {
  id: string;
  name: string;
  checked: boolean;
  relatedOldCategories: string[]; // 對應的舊制類別代碼
}

// 舊制類別（16類）
interface OldDisabilityCategory {
  code: string;
  name: string;
  checked: boolean;
  newCategoryId: string; // 對應的新制類別ID
  severityLevel?: 'mild' | 'moderate' | 'severe' | 'profound' | null; // 嚴重程度
  manualTreatmentCode?: string;
  manualAdd?: number;
  manualServiceMode?: ServiceModeKey | 'any';
  linkedByManual?: boolean;
}

type ServiceModeKey = 'in-clinic' | 'medical-team' | 'home-care' | 'mobile-clinic';

@Component({
  selector: 'app-dental-disability-memo',
  templateUrl: './dental-disability-memo.component.html',
  styleUrls: ['./dental-disability-memo.component.scss']
})
export class DentalDisabilityMemoComponent implements OnInit, OnChanges {

  @Input() isDialogOpened: boolean = false;
  @Input() histRcd: any; // WholeHist object from parent
  @Input() patientData: DisabilityMemoData = {
    patientNo: '',
    patientName: '',
    patientCId: '',
    disabilityCategories: [],
    oldDisabilityCategories: [],
    severityLevels: {},
    memo: '',
    treatmentItems: [],
    specialRequirements: '',
    leftImageUrl: '',
    rightImageUrl: '',
    createdDate: '',
    modifiedDate: ''
  };
  @Output() resultEmit = new EventEmitter<DentalDisabilityFormDataOptimized>();
  @Output() closeEmit = new EventEmitter<void>();

  // Form Data
  memoData: DisabilityMemoData = {
    patientNo: '',
    patientName: '',
    patientCId: '',
    disabilityCategories: [],
    oldDisabilityCategories: [],
    severityLevels: {},
    memo: '',
    treatmentItems: [],
    specialRequirements: '',
    leftImageUrl: '',
    rightImageUrl: '',
    createdDate: '',
    modifiedDate: ''
  };

  // Image upload states
  leftImagePreview: string | null = null;
  rightImagePreview: string | null = null;
  isDraggingLeft: boolean = false;
  isDraggingRight: boolean = false;

  // UI State
  isEditMode: boolean = false;
  isSaving: boolean = false;
  selectedServiceMode: ServiceModeKey = 'in-clinic';
  matchedTreatmentItem: string = ''; // 符合的治療項目代號（單一值）
  matchedDisabilityTypeCodes: string = ''; // 符合的障礙別代碼字串
  quickSelectInput: string = '';
  quickSelectTags: Array<{ display: string; code: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' }> = [];

  @ViewChild('quickSelectBox') quickSelectBox!: ElementRef<HTMLDivElement>;

  // 新制類別（8類）
  newDisabilityCategories: NewDisabilityCategory[] = [
    { id: 'cat1', name: '第一類：神經系統構造及精神、心智功能', checked: false, relatedOldCategories: ['06', '09', '10', '11', '12', '14'] },
    { id: 'cat2', name: '第二類：眼、耳及相關構造與感官功能', checked: false, relatedOldCategories: ['01', '02', '03'] },
    { id: 'cat3', name: '第三類：涉及聲音與言語構造及其功能', checked: false, relatedOldCategories: ['04'] },
    { id: 'cat4', name: '第四類：循環、造血、免疫與呼吸系統', checked: false, relatedOldCategories: ['07-heart'] },
    { id: 'cat5', name: '第五類：消化、新陳代謝與內分泌系統', checked: false, relatedOldCategories: ['07-digest'] },
    { id: 'cat6', name: '第六類：泌尿與生殖系統相關構造', checked: false, relatedOldCategories: ['07-urinary'] },
    { id: 'cat7', name: '第七類：神經、肌肉、骨骼之移動相關', checked: false, relatedOldCategories: ['05-cerebral-palsy', '05-cerebral', '05-general'] },
    { id: 'cat8', name: '第八類：皮膚與相關構造及其功能', checked: false, relatedOldCategories: ['08'] },
    { id: 'cat-other', name: '備註類（對應第一至八類）', checked: false, relatedOldCategories: ['13', '15', '16'] },
    { id: 'cat-manual', name: '其他類', checked: false, relatedOldCategories: ['manual-F4', 'manual-dev-delay', 'manual-elderly', 'manual-LC', 'manual-LD'] }
  ];

  // 舊制類別（16類）與嚴重程度
  disabilityRows: Array<{
    newCategoryId: string;
    items: OldDisabilityCategory[];
  }> = [
    {
      newCategoryId: 'cat1',
      items: [
        { code: '06', name: '06 智能障礙', checked: false, newCategoryId: 'cat1', severityLevel: null },
        { code: '09', name: '09 植物人', checked: false, newCategoryId: 'cat1', severityLevel: null },
        { code: '10', name: '10 失智症', checked: false, newCategoryId: 'cat1', severityLevel: null },
        { code: '11', name: '11 自閉症', checked: false, newCategoryId: 'cat1', severityLevel: null },
        { code: '12', name: '12 慢性精神病', checked: false, newCategoryId: 'cat1', severityLevel: null },
        { code: '14', name: '14 頑性癲癇', checked: false, newCategoryId: 'cat1', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat2',
      items: [
        { code: '01', name: '01 視覺障礙', checked: false, newCategoryId: 'cat2', severityLevel: null },
        { code: '02', name: '02 聽覺障礙', checked: false, newCategoryId: 'cat2', severityLevel: null },
        { code: '03', name: '03 平衡機能障礙', checked: false, newCategoryId: 'cat2', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat3',
      items: [
        { code: '04', name: '04 聲音或語言機能障礙', checked: false, newCategoryId: 'cat3', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat4',
      items: [
        { code: '07-heart', name: '07 重要器官失去功能（心/造血/呼吸）', checked: false, newCategoryId: 'cat4', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat5',
      items: [
        { code: '07-digest', name: '07 重要器官失去功能（吞嚥/胃/腸/肝）', checked: false, newCategoryId: 'cat5', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat6',
      items: [
        { code: '07-urinary', name: '07 重要器官失去功能（腎/膀胱）', checked: false, newCategoryId: 'cat6', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat7',
      items: [
        { code: '05-cerebral-palsy', name: '05 腦性麻痺', checked: false, newCategoryId: 'cat7', severityLevel: null },
        { code: '05-cerebral', name: '05 腦傷/脊髓損傷', checked: false, newCategoryId: 'cat7', severityLevel: null },
        { code: '05-general', name: '05 一般肢體障礙', checked: false, newCategoryId: 'cat7', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat8',
      items: [
        { code: '08', name: '08 顏面損傷者', checked: false, newCategoryId: 'cat8', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat-other',
      items: [
        { code: '13', name: '13 多重障礙', checked: false, newCategoryId: 'cat-other', severityLevel: null },
        { code: '15', name: '15 罕見疾病', checked: false, newCategoryId: 'cat-other', severityLevel: null },
        { code: '16', name: '16 其他（染色體異常等）', checked: false, newCategoryId: 'cat-other', severityLevel: null }
      ]
    },
    {
      newCategoryId: 'cat-manual',
      items: [
        { code: 'manual-F4', name: '先天性唇顎裂及顱顏畸形症患者', checked: false, newCategoryId: 'cat-manual', severityLevel: null, manualTreatmentCode: 'F4', manualServiceMode: 'in-clinic' },
        { code: 'manual-dev-delay', name: '發展遲緩兒童', checked: false, newCategoryId: 'cat-manual', severityLevel: null, manualAdd: 1.5, manualServiceMode: 'any' },
        { code: 'manual-elderly', name: '失能老人/長照個案', checked: false, newCategoryId: 'cat-manual', severityLevel: null, manualAdd: 1.3, manualServiceMode: 'any' },
        { code: 'manual-LC', name: '全民健康保險居家醫療照護整合計畫', checked: false, newCategoryId: 'cat-manual', severityLevel: null, manualTreatmentCode: 'LC', manualAdd: null, manualServiceMode: 'home-care' },
        { code: 'manual-LD', name: '出院準備及追蹤管理', checked: false, newCategoryId: 'cat-manual', severityLevel: null, manualTreatmentCode: 'LD', manualAdd: null, manualServiceMode: 'home-care' }
      ]
    }
  ];

  // 嚴重程度選項
  severityOptions = [
    { value: 'mild', label: '輕度' },
    { value: 'moderate', label: '中度' },
    { value: 'severe', label: '重度' },
    { value: 'profound', label: '極重度' }
  ];

  getSeverityOptionsForItem(item: OldDisabilityCategory) {
    return this.severityOptions;
  }

  private isSeverityAllowedForItem(
    item: OldDisabilityCategory,
    severity: 'mild' | 'moderate' | 'severe' | 'profound'
  ): boolean {
    return true;
  }

  private isSeverityPlaceholder(
    item: OldDisabilityCategory,
    severity: 'mild' | 'moderate' | 'severe' | 'profound'
  ): boolean {
    return !this.isSeverityAllowedForItem(item, severity);
  }

  // 障礙別代碼對應表：舊制代碼 + 嚴重度 -> 障礙別代碼
  // // 非16就不輸出的版本
  // disabilityTypeCodes: Record<string, { [key in 'mild' | 'moderate' | 'severe' | 'profound' | 'any']?: string }> = {
  //   '01': { severe: 'M', profound: 'M' }, // 視覺障礙重度以上 -> M
  //   '05-cerebral': { moderate: 'A', severe: 'A', profound: 'A' }, // 腦麻中度以上 -> A
  //   '05-general': { severe: 'L', profound: 'L' }, // 一般肢體障礙重度以上 -> L
  //   '06': { any: 'B' }, // 智能障礙 -> B
  //   '07-heart': { severe: 'P', profound: 'P' }, // 重要器官失去功能（心臟）重度以上 -> P
  //   '07-digest': { severe: 'P', profound: 'P' }, // 重要器官失去功能（消化）重度以上 -> P
  //   '07-urinary': { severe: 'P', profound: 'P' }, // 重要器官失去功能（腎/膀胱）重度以上 -> P
  //   '09': { any: 'J' }, // 植物人 -> J
  //   '10': { any: 'F' }, // 失智症 -> F
  //   '11': { any: 'C' }, // 自閉症 -> C
  //   '12': { moderate: 'E', severe: 'E', profound: 'E' }, // 中度以上精神障礙 -> E
  //   '13': { any: 'G' }, // 多重障礙 -> G
  //   '14': { any: 'H' }, // 頑性癲癇 -> H
  //   '15': { any: 'K' }, // 罕見疾病 -> K
  //   '16': { any: 'D' } // 其他（染色體異常等）-> D
  // };
  // 非16全輸出I的版本
  disabilityTypeCodes: Record<string, { [key in 'mild' | 'moderate' | 'severe' | 'profound' | 'any']?: string }> = {
    '01': { mild: 'I', moderate: 'I', severe: 'M', profound: 'M' }, // 視覺障礙重度以上 -> M
    '02': { any: 'I' }, // 聽覺障礙 -> I (其他)
    '03': { any: 'I' }, // 平衡機能障礙 -> I (其他)
    '04': { any: 'I' }, // 聲音或語言機能障礙 -> I (其他)
    '05-cerebral-palsy': { any: 'A' }, // 腦性麻痺 -> A
    '05-cerebral': { mild: 'I', moderate: 'Q', severe: 'Q', profound: 'Q' }, // 腦傷/脊髓損傷中度以上 -> Q
    '05-general': { mild: 'I', moderate: 'I', severe: 'L', profound: 'L' }, // 一般肢體障礙重度以上 -> L
    '06': { any: 'B' }, // 智能障礙 -> B
    '07-heart': { mild: 'I', moderate: 'I', severe: 'P', profound: 'P' }, // 重要器官失去功能（心臟）重度以上 -> P
    '07-digest': { mild: 'I', moderate: 'I', severe: 'P', profound: 'P' }, // 重要器官失去功能（消化）重度以上 -> P
    '07-urinary': { mild: 'I', moderate: 'I', severe: 'P', profound: 'P' }, // 重要器官失去功能（腎/膀胱）重度以上 -> P
    '08': { any: 'I' }, // 顏面損傷 -> I (其他)
    '09': { any: 'J' }, // 植物人 -> J
    '10': { any: 'F' }, // 失智症 -> F
    '11': { any: 'C' }, // 自閉症 -> C
    '12': { mild: 'I', moderate: 'E', severe: 'E', profound: 'E' }, // 中度以上精神障礙 -> E
    '13': { any: 'G' }, // 多重障礙 -> G
    '14': { any: 'H' }, // 頑性癲癇 -> H
    '15': { any: 'K' }, // 罕見疾病 -> K
    '16': { any: 'D' } // 其他（染色體異常等）-> D
  };

  /**
   * 判斷是否為可以自動計算的多重障礙項（代碼 13）
   */
  isAutoComputedItem(item: OldDisabilityCategory): boolean {
    return item.code === '13';
  }

  /**
   * 判斷是否為不列入多重障礙評分的項（代碼 15、16）
   */
  isExcludedFromMultiple(item: OldDisabilityCategory): boolean {
    return item.code === '15' || item.code === '16' || item.newCategoryId === 'cat-manual';
  }

  /**
   * 計算多重障礙評分（舊制代碼 13）
   * 根據規則 a-e 計算最終的嚴重度
   * 此方法被 getCode13AutoSeverity() 調用
   */
  computeMultipleDisabilityScore(): { code: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' | null } | null {
    // 蒐集所有被勾選且有嚴重度的障礙（排除 13、15、16）
    const selectedItems: Array<{ code: string; newCategoryId: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' }> = [];

    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (item.checked && item.severityLevel && !this.isAutoComputedItem(item) && !this.isExcludedFromMultiple(item)) {
          // 對於 05-cerebral-palsy、05-cerebral 和 05-general，統一視為 cat7
          const categoryId = item.newCategoryId === 'cat7' ? 'cat7' : item.newCategoryId;
          selectedItems.push({
            code: item.code,
            newCategoryId: categoryId,
            severity: item.severityLevel
          });
        }
      });
    });

    // 只有勾選 2 列以上舊制障礙才計算多重障礙評分
    if (selectedItems.length < 2) {
      return null; // 沒有足夠的障礙項目進行多重障礙評分
    }

    // 判斷是否跨多個新制類別
    const uniqueCategories = new Set(selectedItems.map(item => item.newCategoryId));
    const categoriesCount = uniqueCategories.size;

    // 規則 a、b、c、d、e 的實現
    if (categoriesCount >= 2) {
      // 跨兩種或更多新制類別（規則 a）
      return this.computeMultipleCategoriesSeverity(selectedItems);
    } else {
      // 同一新制類別中多重障礙（規則 b、c、d、e）
      return this.computeSingleCategorySeverity(selectedItems);
    }
  }

  /**
   * 跨多個新制類別計算嚴重度（規則 a）
   */
  private computeMultipleCategoriesSeverity(
    items: Array<{ code: string; newCategoryId: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' }>
  ): { code: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' } {
    // 按嚴重度排序：profound > severe > moderate > mild
    const severityRank = { profound: 4, severe: 3, moderate: 2, mild: 1 };
    const sorted = [...items].sort((a, b) => severityRank[b.severity] - severityRank[a.severity]);

    // 最嚴重的嚴重度
    const maxSeverity = sorted[0].severity;

    // 檢查是否有同級嚴重度
    const hasSameSeverity = sorted.some((item, idx) => idx > 0 && item.severity === sorted[0].severity);

    if (hasSameSeverity) {
      // 有同級嚴重度，升一級
      const severityUpgrade = { mild: 'moderate', moderate: 'severe', severe: 'profound', profound: 'profound' } as const;
      return { code: '13', severity: severityUpgrade[maxSeverity] };
    } else {
      // 沒有同級，直接取最嚴重的
      return { code: '13', severity: maxSeverity };
    }
  }

  /**
   * 同一新制類別計算嚴重度（規則 b、c、d、e）
   */
  private computeSingleCategorySeverity(
    items: Array<{ code: string; newCategoryId: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' }>
  ): { code: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' } {
    const categoryId = items[0].newCategoryId;
    const severityRank = { profound: 4, severe: 3, moderate: 2, mild: 1 };

    // 規則 d：第一類（cat1）有四種以上且最嚴重的兩種相當，升一級
    if (categoryId === 'cat1' && items.length >= 4) {
      const sorted = [...items].sort((a, b) => severityRank[b.severity] - severityRank[a.severity]);
      if (sorted[0].severity === sorted[1].severity) {
        // 最嚴重的兩種相當，升一級
        const severityUpgrade = { mild: 'moderate', moderate: 'severe', severe: 'profound', profound: 'profound' } as const;
        return { code: '13', severity: severityUpgrade[sorted[0].severity] };
      } else {
        // 取最嚴重的
        return { code: '13', severity: sorted[0].severity };
      }
    }

    // 規則 e：第二類（cat2）有多種且最嚴重的兩種相當，升一級
    if (categoryId === 'cat2' && items.length >= 2) {
      const sorted = [...items].sort((a, b) => severityRank[b.severity] - severityRank[a.severity]);
      if (sorted[0].severity === sorted[1].severity) {
        // 最嚴重的兩種相當，升一級
        const severityUpgrade = { mild: 'moderate', moderate: 'severe', severe: 'profound', profound: 'profound' } as const;
        return { code: '13', severity: severityUpgrade[sorted[0].severity] };
      } else {
        // 取最嚴重的
        return { code: '13', severity: sorted[0].severity };
      }
    }

    // 規則 b、c：其他情況，取最嚴重的
    const maxSeverity = [...items].reduce((max, item) => {
      return severityRank[item.severity] > severityRank[max.severity] ? item : max;
    }).severity;

    return { code: '13', severity: maxSeverity };
  }

  /**
   * 判斷指定舊制代碼與嚴重程度是否為案類16需著色
   */
  isSeverityHighlighted(item: OldDisabilityCategory, severityValue: string): boolean {
    const code = item.code;
    const val = severityValue as 'mild' | 'moderate' | 'severe' | 'profound';

    if (item.newCategoryId === 'cat-manual') {
      // 失能老人在「院內」和「醫缺巡迴或社區醫療站」排陖16案類
      if (item.code === 'manual-elderly' &&
          (this.selectedServiceMode === 'in-clinic' || this.selectedServiceMode === 'mobile-clinic')) {
        return false;
      }
      return true;
    }

    // 01 視覺：重度、極重度
    if (code === '01') {
      return val === 'severe' || val === 'profound';
    }

    // 05 肢體：
    // - 05-cerebral-palsy (腦性麻痺)：輕度至極重度皆為16案類
    // - 05-cerebral (腦傷/脊髓損傷)：中度、重度、極重度為16案類
    // - 05-general 一般肢體障礙：重度以上
    if (code === '05-cerebral-palsy') {
      return true; // 四個嚴重度皆為16案類
    }
    if (code === '05-cerebral') {
      return val === 'moderate' || val === 'severe' || val === 'profound';
    }
    if (code === '05-general') {
      return val === 'severe' || val === 'profound';
    }

    // 07 重要器官失去功能：重度、極重度（所有 07 前綴）
    if (code.startsWith('07')) {
      return val === 'severe' || val === 'profound';
    }

    // 12 慢性精神病：中度、重度、極重度
    if (code === '12') {
      return val === 'moderate' || val === 'severe' || val === 'profound';
    }

    // 06/09/10/11/13/14/15/16：輕度至極重度皆適用
    // 例外：09 植物人輕度在居家模式下排除16案類
    const allLevelsCodes = ['06', '09', '10', '11', '13', '14', '15', '16'];
    if (allLevelsCodes.includes(code)) {
      if (code === '09' && val === 'mild' && this.selectedServiceMode === 'home-care') {
        return false;
      }
      return val === 'mild' || val === 'moderate' || val === 'severe' || val === 'profound';
    }

    return false;
  }

  treatmentItemsList: Array<{ text: string; value: string; checked: boolean }> = [
    { text: '需要輔助儀器', value: 'assist_device', checked: false },
    { text: '需要陪同人員', value: 'companion', checked: false },
    { text: '特殊體位安排', value: 'special_position', checked: false },
    { text: '延長看診時間', value: 'extended_time', checked: false },
    { text: '分次治療', value: 'divided_treatment', checked: false }
  ];

  // 治療服務模式對應的治療項目代號
  treatmentServiceMapping: Record<ServiceModeKey, { label: string; items: Record<string, string[]> }> = {
    'in-clinic': {
      label: '院內',
      items: {
        'non-psychiatric-profound': ['FG'],
        'non-psychiatric-severe': ['FH'],
        'non-psychiatric-moderate': ['FI'],
        'non-psychiatric-mild': ['FJ'],
        'psychiatric-severe-above': ['FC'],
        'psychiatric-moderate': ['FD'],
        'developmental-delay': ['FV'],
        'cerebral-limb': ['LF'],
        'autism-dementia': ['LN']
      }
    },
    'medical-team': {
      label: '醫療團',
      items: {
        'non-psychiatric-profound': ['FK'],
        'non-psychiatric-severe': ['FL'],
        'non-psychiatric-moderate': ['FM'],
        'non-psychiatric-mild': ['FN'],
        'psychiatric-severe-above': ['FE'],
        'psychiatric-moderate': ['FF'],
        'developmental-delay': ['FX'],
        'elderly': ['L1'],
        'autism-dementia': ['LP'],
        'other-disabled': ['LT']
      }
    },
    'home-care': {
      label: '居家',
      items: {
        'non-psychiatric-profound': ['FS'],
        'non-psychiatric-severe': ['FY'],
        'non-psychiatric-moderate': ['L4'],
        'developmental-delay': ['LE'],
        'elderly': ['L2'],
        'integrated-care': ['LC']
      }
    },
    'mobile-clinic': {
      label: '醫缺巡迴或社區醫療站',
      items: {
        'non-psychiatric-profound': ['L5'],
        'non-psychiatric-severe': ['L6'],
        'non-psychiatric-moderate': ['L7'],
        'non-psychiatric-mild': ['L8'],
        'psychiatric-severe-above': ['L9'],
        'psychiatric-moderate': ['LA'],
        'autism-dementia': ['LS']
      }
    }
  };

  serviceModeOptions = [
    { value: 'in-clinic', label: '院內', disabled: false },
    { value: 'medical-team', label: '醫療團', disabled: false },
    { value: 'home-care', label: '居家', disabled: false },
    { value: 'mobile-clinic', label: '醫缺巡迴或社區醫療站', disabled: false }
  ];

  // ICF 編碼對照表（新制身心障礙證明）
  private icfCodeMapping: { [key: string]: string } = {
    // 第一類 神經系統構造及精神、心智功能
    'b110': '06',    // 意識功能 -> 智能障礙者
    'b117': '09',    // 智力功能 -> 植物人
    'b122': '10',    // 整體心理社會功能 -> 失智症者
    'b139': '11',    // 整體心理功能 -> 自閉症者
    'b140': '14',    // 注意力功能 -> 頑性癲癇
    'b144': '14',    // 記憶功能 -> 頑性癲癇
    'b147': '14',    // 心理動作功能 -> 頑性癲癇
    'b152': '14',    // 情緒功能 -> 頑性癲癇
    'b160': '14',    // 思想功能 -> 頑性癲癇
    'b164': '14',    // 高階認知功能 -> 頑性癲癇
    'b167': '14',    // 語言功能 -> 頑性癲癇
    'b16701': '14',  // 閱讀功能 -> 頑性癲癇
    'b16711': '14',  // 書寫功能 -> 頑性癲癇
    // 第二類 眼、耳及相關構造與感官功能及疼痛
    'b210': '01',    // 視覺功能 -> 視覺障礙者
    'b230': '02',    // 聽覺功能 -> 聽覺機能障礙者
    'b235': '03',    // 平衡功能 -> 平衡機能障礙者
    // 第三類 涉及聲音與語言構造及其功能
    'b310': '04',    // 嗓音功能 -> 聲音機能或語言機能障礙者
    'b320': '04',    // 構音功能 -> 聲音機能或語言機能障礙者
    'b330': '04',    // 言語功能的流暢和節律 -> 聲音機能或語言機能障礙者
    's320': '04',    // 口結構 -> 聲音機能或語言機能障礙者
    's330': '04',    // 咽結構 -> 聲音機能或語言機能障礙者
    's340': '04',    // 喉結構 -> 聲音機能或語言機能障礙者
    // 第四類 循環、造血、免疫與呼吸系統構造及其功能
    'b415': '07-heart',   // 血液功能 -> 重要器官失去功能者－心臟
    'b430': '07-heart',   // 血液系統功能 -> 重要器官失去功能者－造血機能
    'b440': '07-heart',   // 呼吸功能 -> 重要器官失去功能者－呼吸器官
    's430': '07-heart',   // 呼吸系統結構 -> 重要器官失去功能者－呼吸器官
    // 第五類 消化、新陳代謝與內分泌系統相關構造及其功能
    'b510': '07-digest',  // 攝食功能 -> 重要器官失去功能者－吞嚥功能
    's530': '07-digest',  // 胃構造 -> 重要器官失去功能者－胃
    's540': '07-digest',  // 腸道結構 -> 重要器官失去功能者－腸道
    's560': '07-digest',  // 肝臟結構 -> 重要器官失去功能者－肝臟
    // 第六類 泌尿與生殖系統相關構造及其功能
    'b610': '07-urinary', // 尿液排泄功能 -> 重要器官失去功能者－腎臟
    'b620': '07-urinary', // 排尿功能 -> 重要器官失去功能者－膀胱
    // 第七類 神經、肌肉、骨骼之移動相關構造及其功能
    'b730': '05-general', // 肌肉力量功能 -> 肢體障礙者
    'b735': '05-general', // 肌肉張力功能 -> 肢體障礙者
    'b765': '05-general', // 不隨意動作功能 -> 肢體障礙者
    's730': '05-general', // 上肢結構 -> 肢體障礙者
    's760': '05-general', // 下肢結構/軀幹 -> 肢體障礙者
    // 第八類 皮膚與相關構造及其功能
    'b810': '08',    // 皮膚保護功能 -> 顏面損傷者
    'b830': '08',    // 皮膚其他功能 -> 顏面損傷者
    's810': '08',    // 皮膚區域結構 -> 顏面損傷者
  };

  private editSnapshot: any = null;

  constructor(
    private userConfirm: UserConfirmService,
    private dentalDisabilityService: DentalDisabilityMemoService
  ) {}

  // 需要在文件頂部添加 import
  // import { DentalDisabilityMemoService } from './dental-disability-memo.service';

  ngOnInit(): void {
    this.initializeData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isDialogOpened && this.patientData) {
      this.loadPatientData();
    }
  }

  /**
   * 初始化數據
   */
  private initializeData(): void {
    this.resetTreatmentItems();
  }

  /**
   * 加載患者數據
   */
  private loadPatientData(): void {
    if (this.patientData) {
      this.memoData = {
        ...this.memoData,
        ...this.patientData
      };
      // 恢復選中的treatment items
      if (this.memoData.treatmentItems && this.memoData.treatmentItems.length > 0) {
        this.treatmentItemsList.forEach(item => {
          item.checked = this.memoData.treatmentItems!.includes(item.value);
        });
      }
      // 恢復身心障礙類別選擇
      if (this.memoData.disabilityCategories && this.memoData.disabilityCategories.length > 0) {
        this.newDisabilityCategories.forEach(cat => {
          cat.checked = this.memoData.disabilityCategories!.includes(cat.id);
        });
      }
      // 恢復舊制類別和嚴重程度
      if (this.memoData.oldDisabilityCategories && this.memoData.oldDisabilityCategories.length > 0) {
        this.disabilityRows.forEach(row => {
          row.items.forEach(item => {
            item.checked = this.memoData.oldDisabilityCategories!.includes(item.code);
            if (this.memoData.severityLevels && this.memoData.severityLevels[item.code]) {
              item.severityLevel = this.memoData.severityLevels[item.code] as any;
              if (item.severityLevel && !this.isSeverityAllowedForItem(item, item.severityLevel)) {
                item.severityLevel = null;
                item.checked = false;
              }
            }
          });
        });
      }
      // 恢復圖片
      if (this.memoData.leftImageUrl) {
        this.leftImagePreview = this.memoData.leftImageUrl;
      }
      if (this.memoData.rightImageUrl) {
        this.rightImagePreview = this.memoData.rightImageUrl;
      }

      // 重置快選顯示
      this.quickSelectTags = [];
      this.quickSelectInput = '';
      if (this.quickSelectBox && this.quickSelectBox.nativeElement) {
        this.quickSelectBox.nativeElement.innerText = '';
      }

      // 恢復服務模式
      if ((this.memoData as any).selectedServiceMode) {
        this.selectedServiceMode = (this.memoData as any).selectedServiceMode;
      }

      // 恢復快選標籤
      if ((this.memoData as any).quickSelectTags && (this.memoData as any).quickSelectTags.length > 0) {
        this.quickSelectTags = (this.memoData as any).quickSelectTags.map((t: any) => ({ ...t }));
      }

      this.updateServiceModeLockByManualHomeCare();

      // 恢復治療代號計算結果
      this.updateMatchedTreatmentItems();
    }
  }

  /**
   * 重置 treatment items
   */
  private resetTreatmentItems(): void {
    this.treatmentItemsList.forEach(item => {
      item.checked = false;
    });
  }

  /**
   * 編輯模式開關
   */
  toggleEditMode(): void {
    // 進入編輯模式前建立快照
    if (!this.isEditMode) {
      this.captureSnapshot();
    }
    this.isEditMode = !this.isEditMode;
  }

  /**
   * 檢查整行是否有任何項目被勾選
   */
  isRowAnyItemChecked(row: any): boolean {
    return row.items && row.items.some((item: OldDisabilityCategory) => item.checked);
  }

  /**
   * 檢查整行是否應該顯示（在非編輯狀態下，如果沒有任何項目被勾選則隱藏）
   */
  shouldShowRow(row: any): boolean {
    if (this.isEditMode) {
      return true; // 編輯模式下顯示所有行
    }
    return this.isRowAnyItemChecked(row); // 非編輯模式下，只顯示有選中項目的行
  }

  /**
   * 檢查新制類別是否被選中
   */
  isNewCategoryChecked(newCategoryId: string): boolean {
    const category = this.newDisabilityCategories.find(cat => cat.id === newCategoryId);
    return category ? category.checked : false;
  }

  /**
   * 檢查舊制類別項目是否應該顯示
   */
  shouldShowOldCategoryItem(item: OldDisabilityCategory): boolean {
    if (this.isEditMode) {
      return true; // 編輯模式下顯示所有項目
    }
    return item.checked; // 非編輯模式下，只顯示被選中的項目
  }

  /**
   * 判斷是否為代碼 13 行（多重障礙自動計算行）
   */
  isCode13Item(item: OldDisabilityCategory): boolean {
    return item.code === '13';
  }

  /**
   * 判斷是否為代碼 15 或 16（手動選擇行）
   */
  isManualSelectItem(item: OldDisabilityCategory): boolean {
    return item.code === '15' || item.code === '16';
  }

  /**
   * 取得代碼 13 的自動計算嚴重度值
   */
  getCode13AutoSeverity(): 'mild' | 'moderate' | 'severe' | 'profound' | null {
    const result = this.computeMultipleDisabilityScore();
    return result ? result.severity : null;
  }

  /**
   * 取得代碼 13 的自動計算嚴重度標籤
   */
  getCode13AutoSeverityLabel(): string {
    const severity = this.getCode13AutoSeverity();
    if (!severity) return '--';
    const labels: Record<'mild' | 'moderate' | 'severe' | 'profound', string> = {
      mild: '輕度',
      moderate: '中度',
      severe: '重度',
      profound: '極重度'
    };
    return labels[severity];
  }

  /**
   * 處理新制類別勾選
   */
  onNewCategoryChange(category: NewDisabilityCategory): void {
    if (!category.checked) {
      // 取消勾選：清除相關的舊制類別和嚴重程度
      this.disabilityRows.forEach(row => {
        row.items.forEach(item => {
          if (category.relatedOldCategories.includes(item.code)) {
            item.checked = false;
            item.severityLevel = null;
            if (item.newCategoryId === 'cat-manual') {
              this.syncLegacyFromManualSelection(item);
            }
          }
        });
      });

      // 同步更新特殊治療代號和障礙別代碼
      this.updateMatchedTreatmentItems();
    }
  }

  /**
   * 處理舊制類別勾選
   */
  onOldCategoryChange(oldItem: OldDisabilityCategory): void {
    if (oldItem.checked) {
      // 勾選：自動勾選對應的新制類別
      const newCat = this.newDisabilityCategories.find(cat => cat.id === oldItem.newCategoryId);
      if (newCat) {
        newCat.checked = true;
      }
    } else {
      // 取消勾選：清除嚴重程度，檢查新制類別是否還有其他選中項
      oldItem.severityLevel = null;

      // 同時移除對應的快選標籤（如果有的話）
      const tagIndex = this.quickSelectTags.findIndex(tag => tag.code === oldItem.code);
      if (tagIndex !== -1) {
        this.quickSelectTags.splice(tagIndex, 1);
      }

      const hasOtherChecked = this.disabilityRows
        .find(row => row.newCategoryId === oldItem.newCategoryId)
        ?.items.some(item => item.code !== oldItem.code && item.checked);

      if (!hasOtherChecked) {
        const newCat = this.newDisabilityCategories.find(cat => cat.id === oldItem.newCategoryId);
        if (newCat) {
          newCat.checked = false;
        }
      }
    }

    this.syncLegacyFromManualSelection(oldItem);
    this.updateServiceModeLockByManualHomeCare();

    // 更新符合的治療項目代號
    this.updateMatchedTreatmentItems();
  }

  /**
   * 處理嚴重程度選擇
   */
  onSeverityChange(oldItem: OldDisabilityCategory, severity: string | null): void {
    if (severity && !this.isSeverityAllowedForItem(oldItem, severity as any)) {
      return;
    }
    oldItem.severityLevel = severity as any;

    if (severity) {
      // 有選擇嚴重程度：自動勾選舊制和新制類別
      oldItem.checked = true;
      const newCat = this.newDisabilityCategories.find(cat => cat.id === oldItem.newCategoryId);
      if (newCat) {
        newCat.checked = true;
      }
    } else {
      // 清除嚴重程度：檢查是否還有其他嚴重程度被選擇
      const hasOtherSeverity = this.disabilityRows
        .find(row => row.newCategoryId === oldItem.newCategoryId)
        ?.items.some(item => item.code !== oldItem.code && item.severityLevel);

      if (!hasOtherSeverity) {
        oldItem.checked = false;
        const newCat = this.newDisabilityCategories.find(cat => cat.id === oldItem.newCategoryId);
        if (newCat) {
          newCat.checked = false;
        }
      }
    }

    this.syncLegacyFromManualSelection(oldItem);
    this.updateServiceModeLockByManualHomeCare();

    // 更新符合的治療項目代號
    this.updateMatchedTreatmentItems();
  }

  private syncLegacyFromManualSelection(oldItem: OldDisabilityCategory): void {
    if (oldItem.newCategoryId !== 'cat-manual') {
      return;
    }

    const legacy16 = this.findOldCategoryItem('16');
    if (!legacy16) {
      return;
    }

    const catOther = this.newDisabilityCategories.find(cat => cat.id === 'cat-other');

    // 以狀態為基礎重新評估：從所有 manual 項目中找出最佳連動嚴重度
    const bestSeverity = this.findBestManualLinkSeverity();

    if (bestSeverity) {
      // 仍有 manual 項目需要連動 legacy 16
      legacy16.checked = true;
      legacy16.severityLevel = bestSeverity;
      legacy16.linkedByManual = true;
      if (catOther) {
        catOther.checked = true;
      }
    } else if (legacy16.linkedByManual) {
      // 無任何 manual 項目需要連動，解除 legacy 16
      legacy16.checked = false;
      legacy16.severityLevel = null;
      legacy16.linkedByManual = false;
      if (catOther && !this.hasAnyCheckedInCategory('cat-other')) {
        catOther.checked = false;
      }
    }
  }

  private hasAnyCheckedInCategory(categoryId: string): boolean {
    const row = this.disabilityRows.find(targetRow => targetRow.newCategoryId === categoryId);
    if (!row) {
      return false;
    }
    return row.items.some(item => item.checked);
  }

  private getLinkedSeverityForManualItem(
    item: OldDisabilityCategory
  ): 'mild' | 'moderate' | 'severe' | 'profound' | null {
    switch (item.code) {
      case 'manual-F4':
        return item.severityLevel || null;
      case 'manual-dev-delay':
        return 'moderate';
      case 'manual-elderly':
        return 'mild';
      default:
        return null;
    }
  }

  private hasManualLinkForSeverity(
    severity: 'mild' | 'moderate' | 'severe' | 'profound'
  ): boolean {
    return this.disabilityRows.some(row =>
      row.items.some(item =>
        item.newCategoryId === 'cat-manual' &&
        item.checked &&
        item.severityLevel &&
        this.getLinkedSeverityForManualItem(item) === severity
      )
    );
  }

  /**
   * 從所有已勾選且有嚴重度的 manual 項目中，找出最佳連動嚴重度
   * 取嚴重度最高者作為 legacy 16 的連動嚴重度
   */
  private findBestManualLinkSeverity(): 'mild' | 'moderate' | 'severe' | 'profound' | null {
    const severityRank: Record<string, number> = { profound: 4, severe: 3, moderate: 2, mild: 1 };
    let best: 'mild' | 'moderate' | 'severe' | 'profound' | null = null;

    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (item.newCategoryId === 'cat-manual' && item.checked && item.severityLevel) {
          const linked = this.getLinkedSeverityForManualItem(item);
          if (linked && (!best || severityRank[linked] > severityRank[best])) {
            best = linked;
          }
        }
      });
    });

    return best;
  }

  private findOldCategoryItem(code: string): OldDisabilityCategory | null {
    for (const row of this.disabilityRows) {
      const match = row.items.find(item => item.code === code);
      if (match) return match;
    }
    return null;
  }

  private updateServiceModeLockByManualHomeCare(): void {
    const shouldLockHomeCare = this.isManualHomeCareSelected();
    const shouldLockInClinic = this.isManualInClinicSelected();

    this.serviceModeOptions.forEach(option => {
      if (shouldLockHomeCare) {
        option.disabled = option.value !== 'home-care';
      } else if (shouldLockInClinic) {
        option.disabled = option.value !== 'in-clinic';
      } else {
        option.disabled = false;
      }
    });

    if (shouldLockHomeCare) {
      this.selectedServiceMode = 'home-care';
    } else if (shouldLockInClinic) {
      this.selectedServiceMode = 'in-clinic';
    }
  }

  private isManualHomeCareSelected(): boolean {
    return this.disabilityRows.some(row =>
      row.items.some(item =>
        item.newCategoryId === 'cat-manual' &&
        (item.code === 'manual-LC' || item.code === 'manual-LD') &&
        item.checked
      )
    );
  }

  private isManualInClinicSelected(): boolean {
    return this.disabilityRows.some(row =>
      row.items.some(item =>
        item.newCategoryId === 'cat-manual' &&
        item.code === 'manual-F4' &&
        item.checked
      )
    );
  }

  /**
   * 判斷指定項目是否應被禁用（互斥邏輯）
   * F4（唇顎裂）與 LC/LD（居家照護/出院追蹤）互斥
   * 發展遲緩兒童 與 失能老人/長照個案 互斥
   */
  isItemDisabled(item: OldDisabilityCategory): boolean {
    if (item.code === 'manual-F4') {
      return this.isManualHomeCareSelected();
    }
    if (item.code === 'manual-LC' || item.code === 'manual-LD') {
      return this.isManualInClinicSelected();
    }
    // 發展遲緩兒童 ↔ 失能老人/長照個案 互斥
    if (item.code === 'manual-dev-delay') {
      const elderly = this.findOldCategoryItem('manual-elderly');
      return !!(elderly?.checked);
    }
    if (item.code === 'manual-elderly') {
      const devDelay = this.findOldCategoryItem('manual-dev-delay');
      return !!(devDelay?.checked);
    }
    return false;
  }

  /**
   * 快選輸入 Enter 處理
   */
  async handleQuickSelectEnter(event: KeyboardEvent): Promise<void> {
    event.preventDefault();
    const text = (event.target as HTMLElement).innerText.trim();
    if (!text) {
      return;
    }

    const parsed = this.parseQuickSelect(text);
    if (!parsed) {
      this.showInfo('非合法字符');
      return;
    }

    await this.addQuickTag(parsed.display, parsed.code, parsed.severityDigit);
    // 清空輸入框
    (event.target as HTMLElement).innerText = '';
    this.quickSelectInput = '';
  }

  /**
   * 快選輸入內容同步
   */
  handleQuickSelectInput(event: Event): void {
    this.quickSelectInput = (event.target as HTMLElement).innerText || '';
  }

  /**
   * 快選 Backspace / Delete 刪除最近的標籤
   */
  handleQuickSelectKeydown(event: KeyboardEvent): void {
    const isEnter = event.key === 'Enter';
    if (isEnter) {
      this.handleQuickSelectEnter(event);
      return;
    }

    const isBackspace = event.key === 'Backspace';
    const isDelete = event.key === 'Delete';
    if (!isBackspace && !isDelete) {
      return;
    }

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const offset = range.startOffset;
    const containerText = (range.startContainer.textContent || '').length;

    // 游標在開頭按 Backspace 或在結尾按 Delete 時，刪除最後一個標籤
    if ((isBackspace && offset === 0) || (isDelete && offset === containerText)) {
      if (this.quickSelectTags.length > 0) {
        event.preventDefault();
        this.removeQuickTag(this.quickSelectTags.length - 1);
      }
    }
  }

  /**
   * 解析快選字串
   */
  private parseQuickSelect(text: string): { display: string; code: string; severityDigit: number } | null {
    const normalized = text.replace(/\s+/g, '').replace(/換/g, '').toUpperCase();

    // 格式 1: 舊制代碼.嚴重度 (例如: 01.1, 16.4)
    const oldCodeMatch = normalized.match(/^(0[1-9]|1[0-6])\.([1-4])$/);
    if (oldCodeMatch) {
      const code = oldCodeMatch[1];
      const severityDigit = parseInt(oldCodeMatch[2], 10);
      if (code === '05' && (severityDigit === 3 || severityDigit === 4)) {
        return { display: `05-general.${severityDigit}`, code: '05-general', severityDigit };
      }
      return { display: `${code}.${severityDigit}`, code, severityDigit };
    }

    // 格式 2: 純嚴重度 (例如: .1, .2, .3, .4) - 需要搭配前一個選擇使用
    const severityOnlyMatch = normalized.match(/^\.([1-4])$/);
    if (severityOnlyMatch) {
      // 這個格式需要有前置的類別選擇，這裡暫不處理
      // 可以在未來擴充時提示使用者需先選擇類別
      return null;
    }

    // 格式 3: 特殊三碼 074 -> 07-urinary (膀胱)
    if (normalized === '074') {
      // 074 對應「07 重要器官失去功能（腎/膀胱）」中的膀胱
      // 由於 07 有多個細分，這裡直接返回 null，讓後續 mapInputCodeToItemCode 處理
      // 或者直接對應到 07-urinary
      return null; // 需要搭配 .1-.4 嚴重度
    }

    // 格式 4: ICF 編碼 (例如: b110, s760, b16701) + 可選嚴重度
    const icfMatch = normalized.match(/^([BS]\d{3,5})(?:\.([1-4]))?$/);
    if (icfMatch) {
      const icfCode = icfMatch[1].toLowerCase(); // 轉小寫以匹配對照表
      const severityDigit = icfMatch[2] ? parseInt(icfMatch[2], 10) : null;

      // 查找 ICF 編碼對應的舊制代碼
      const oldCode = this.icfCodeMapping[icfCode];
      if (oldCode) {
        if (severityDigit) {
          return { display: `${icfCode}.${severityDigit}`, code: oldCode, severityDigit };
        } else {
          // 沒有嚴重度，返回 null（需要搭配嚴重度）
          return null;
        }
      }
    }

    // 格式 5: 三碼特殊代碼.嚴重度 (例如: 074.1)
    const specialCodeMatch = normalized.match(/^(074)\.([1-4])$/);
    if (specialCodeMatch) {
      const severityDigit = parseInt(specialCodeMatch[2], 10);
      return { display: `074.${severityDigit}`, code: '07-urinary', severityDigit };
    }

    return null;
  }

  /**
   * 新增快選標籤並套用到表單
   */
  private async addQuickTag(display: string, inputCode: string, severityDigit: number): Promise<void> {
    const severity = this.severityDigitToValue(severityDigit);
    if (!severity) {
      this.showInfo('非合法字符');
      return;
    }

    const targetCode = await this.mapInputCodeToItemCode(inputCode);
    if (!targetCode) {
      this.showInfo('找不到對應的舊制類別');
      return;
    }

    // 應用選擇到表單
    this.applyQuickSelection(targetCode, severity);

    // 標籤去重：同 code 只留最後一次設定
    this.quickSelectTags = this.quickSelectTags.filter(t => t.code !== targetCode);
    this.quickSelectTags.push({ display, code: targetCode, severity });
  }

  /**
   * 將快選結果套用到舊制/新制選項
   */
  private applyQuickSelection(targetCode: string, severity: 'mild' | 'moderate' | 'severe' | 'profound'): void {
    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (item.code === targetCode) {
          if (!this.isSeverityAllowedForItem(item, severity)) {
            this.showInfo('此障礙類別不支援該嚴重程度');
            return;
          }
          item.checked = true;
          item.severityLevel = severity;
          const newCat = this.newDisabilityCategories.find(cat => cat.id === item.newCategoryId);
          if (newCat) {
            newCat.checked = true;
          }
        }
      });
    });

    this.updateMatchedTreatmentItems();
  }

  /**
   * 依輸入 code 找到實際 item code（處理多選情況）
   */
  private async mapInputCodeToItemCode(inputCode: string): Promise<string | null> {
    const allItems: OldDisabilityCategory[] = [];
    this.disabilityRows.forEach(r => allItems.push(...r.items));

    // 完全相符先
    const exact = allItems.find(i => i.code === inputCode);
    if (exact) return exact.code;

    // 前綴相符（如 05 -> 05-cerebral-palsy/05-cerebral/05-general，07 -> 07-heart/07-digest/07-urinary）
    const candidates = allItems.filter(i => i.code.startsWith(inputCode));

    if (candidates.length === 0) {
      return null;
    }

    if (candidates.length === 1) {
      return candidates[0].code;
    }

    // 多個候選：讓使用者選擇
    return await this.askUserToChooseCode(inputCode, candidates);
  }

  /**
   * 當有多個舊制選項時，讓使用者選擇
   */
  private async askUserToChooseCode(inputCode: string, candidates: OldDisabilityCategory[]): Promise<string | null> {
    // 動態注入樣式：只在這個對話框期間使用垂直排列
    const styleId = 'quick-select-dialog-style';
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `.dialog-action-btn { flex-direction: column !important; row-gap: 8px !important; }`;
    document.head.appendChild(style);

    try {
      // 建立 customBtnMid 按鈕陣列
      const customBtns = candidates.map(candidate => ({
        text: candidate.name,
        return: candidate.code
      }));

      const result = await this.userConfirm.showConfirm({
        title: '提示',
        msg: `請選擇${inputCode}類別的具體障礙類型：`,
        hideYes: true,
        hideNo: true,
        width: 360,
        customBtnMid: customBtns
      });

      // showConfirm 返回的是按鈕的 return 值，或 false/null 表示取消
      return (result && typeof result === 'string') ? result : null;
    } finally {
      // 對話框關閉後移除臨時樣式
      const tempStyle = document.getElementById(styleId);
      if (tempStyle) {
        document.head.removeChild(tempStyle);
      }
    }
  }


  private severityDigitToValue(digit: number): 'mild' | 'moderate' | 'severe' | 'profound' | null {
    switch (digit) {
      case 1: return 'mild';
      case 2: return 'moderate';
      case 3: return 'severe';
      case 4: return 'profound';
      default: return null;
    }
  }


  private showInfo(msg: string): void {
    if (this.userConfirm && (this.userConfirm as any).showAlert) {
      (this.userConfirm as any).showAlert('提示', msg, { width: 320 });
    } else {
      alert(msg);
    }
  }

  removeQuickTag(idx: number): void {
    const tag = this.quickSelectTags[idx];
    this.quickSelectTags.splice(idx, 1);

    // 清除此標籤對應的舊制勾選與嚴重度
    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (item.code === tag.code) {
          item.checked = false;
          item.severityLevel = null;
        }
      });
    });

    // 同步新制類別勾選狀態
    this.newDisabilityCategories.forEach(cat => {
      const hasChecked = this.disabilityRows
        .find(r => r.newCategoryId === cat.id)
        ?.items.some(item => item.checked);
      cat.checked = !!hasChecked;
    });

    this.updateMatchedTreatmentItems();
  }

  /**
   * 更新符合的治療項目代號
   * 邏輯：
   * 1. 計算勾選的舊制障礙列數（不是新制類別）
   * 2. 單一列 → 直接用該列原本代號映射治療代號
   * 3. 多列 → 計算代碼 13 多重障礙評分，基於評分映射
   */
  private updateMatchedTreatmentItems(): void {
    const modeKey = this.selectedServiceMode;
    const modeMapping = this.treatmentServiceMapping[modeKey];

    if (!modeMapping) {
      this.matchedTreatmentItem = '';
      return;
    }

    const hasAnySelected = this.hasAnySelection();

    // 蒐集所有已選擇的障礙（排除 13、15、16、其他類）
    const selectedItems: Array<{ item: OldDisabilityCategory; categoryId: string }> = [];
    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (item.checked && item.severityLevel && !this.isAutoComputedItem(item) && !this.isExcludedFromMultiple(item)) {
          selectedItems.push({
            item: item,
            categoryId: item.newCategoryId === 'cat7' ? 'cat7' : item.newCategoryId
          });
        }
      });
    });

    // 僅保留符合16案類（有 severity-highlight）的項目
    const eligibleItems = selectedItems.filter(({ item }) =>
      this.isSeverityHighlighted(item, item.severityLevel!)
    );

    const matches: Array<{ code: string; add: number | null; summary: string; manual: boolean; source: string }> = [];

    // 先收集手動輸入的「其他類」
    matches.push(...this.collectManualMatches(modeKey));

    // 依據 CSV 規則挑選對應的自動代碼
    eligibleItems.forEach(({ item }) => {
      const score = this.getScoreForItem(item);
      if (!score) return;

      const explicit = this.getExplicitAutoMatchesForScore(score, modeKey);
      if (explicit.length > 0) {
        matches.push(...explicit);
        return;
      }

      const generic = this.getGenericNonPsychMatch(item, modeKey);
      if (generic) {
        matches.push(generic);
      }
    });

    // 不符合16案類者：僅醫療團輸出 LT
    if (matches.length === 0 && hasAnySelected && modeKey === 'medical-team') {
      matches.push({
        code: 'LT',
        add: 1.2,
        summary: '其他具身心障礙證明者',
        manual: false,
        source: 'fallback'
      });
    }

    if (matches.length > 0) {
      console.log('🧾 [特治代碼] 符合項目清單', matches.map(match => ({
        代碼: match.code,
        加成: match.add,
        摘要: match.summary,
        手動: match.manual,
        來源: match.source
      })));
    }

    const bestMatch = this.pickBestMatch(matches);
    this.matchedTreatmentItem = bestMatch ? bestMatch.code : '';

    // 更新障礙別代碼
    this.updateMatchedDisabilityTypeCodes();
  }

  private hasAnySelection(): boolean {
    return this.disabilityRows.some(row =>
      row.items.some(item => item.checked && item.severityLevel)
    );
  }

  private getSeverityDigit(severity: 'mild' | 'moderate' | 'severe' | 'profound' | null | undefined): number | null {
    switch (severity) {
      case 'mild': return 1;
      case 'moderate': return 2;
      case 'severe': return 3;
      case 'profound': return 4;
      default: return null;
    }
  }

  private getAddFromSeverity(severity: 'mild' | 'moderate' | 'severe' | 'profound'): number {
    switch (severity) {
      case 'mild': return 1.3;
      case 'moderate': return 1.5;
      case 'severe': return 1.9;
      case 'profound': return 2.1;
    }
  }

  private getScoreForItem(item: OldDisabilityCategory): string | null {
    const digit = this.getSeverityDigit(item.severityLevel || null);
    if (!digit) return null;
    return `${item.code}.${digit}`;
  }

  private collectManualMatches(modeKey: ServiceModeKey) {
    const matches: Array<{ code: string; add: number | null; summary: string; manual: boolean; source: string }> = [];

    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (
          item.newCategoryId !== 'cat-manual' ||
          !item.checked ||
          !item.severityLevel
        ) {
          return;
        }

        let code: string | null = item.manualTreatmentCode || null;

        if (item.code === 'manual-dev-delay') {
          const codeByMode = {
            'in-clinic': 'FV',
            'medical-team': 'FX',
            'home-care': 'LE',
            'mobile-clinic': 'LB'
          } as const;
          code = codeByMode[modeKey];
        }

        if (item.code === 'manual-elderly') {
          const codeByMode = {
            'in-clinic': '',
            'medical-team': 'L1',
            'home-care': 'L2',
            'mobile-clinic': ''
          } as const;
          code = codeByMode[modeKey];
        }

        if (item.manualServiceMode && item.manualServiceMode !== 'any' && item.manualServiceMode !== modeKey) {
          return;
        }

        if (!code) {
          return;
        }

        const add = item.manualAdd !== undefined ? item.manualAdd : this.getAddFromSeverity(item.severityLevel);
        matches.push({
          code: code,
          add: add === undefined ? null : add,
          summary: item.name,
          manual: true,
          source: 'manual'
        });
      });
    });

    return matches;
  }

  private getExplicitAutoMatchesForScore(score: string, modeKey: ServiceModeKey) {
    const matches: Array<{ code: string; add: number; summary: string; manual: boolean; source: string }> = [];

    // 精障 12.3 / 12.4
    if (score === '12.3' || score === '12.4') {
      const codeByMode = {
        'in-clinic': 'FC',
        'medical-team': 'FE',
        'mobile-clinic': 'L9'
      } as const;
      const code = codeByMode[modeKey as keyof typeof codeByMode];
      if (code) {
        matches.push({ code, add: 1.5, summary: '精障-重度以上', manual: false, source: 'explicit' });
      }
    }

    // 精障 12.2
    if (score === '12.2') {
      const codeByMode = {
        'in-clinic': 'FD',
        'medical-team': 'FF',
        'mobile-clinic': 'LA'
      } as const;
      const code = codeByMode[modeKey as keyof typeof codeByMode];
      if (code) {
        matches.push({ code, add: 1.5, summary: '精障-中度', manual: false, source: 'explicit' });
      }
    }

    // 自閉症、失智症
    if (/^(10|11)\.[1-4]$/.test(score)) {
      const codeByMode = {
        'in-clinic': 'LN',
        'medical-team': 'LP',
        'mobile-clinic': 'LS'
      } as const;
      const code = codeByMode[modeKey as keyof typeof codeByMode];
      if (code) {
        matches.push({ code, add: 2.1, summary: '自閉/失智', manual: false, source: 'explicit' });
      }
    }

    // 腦傷/脊髓損傷之中度肢體障礙
    if (score === '05-cerebral.2') {
      const codeByMode = {
        'in-clinic': 'LF',
        'medical-team': 'LG',
        'mobile-clinic': 'LH',
        'home-care': 'LJ'
      } as const;
      const code = codeByMode[modeKey as keyof typeof codeByMode];
      if (code) {
        matches.push({ code, add: 1.5, summary: '腦傷/脊髓中度肢體', manual: false, source: 'explicit' });
      }
    }

    // 腦性麻痺：所有嚴重度皆視同一般非精神類別處理，不走 explicit，由 getGenericNonPsychMatch() 處理

    return matches;
  }

  private getGenericNonPsychMatch(item: OldDisabilityCategory, modeKey: ServiceModeKey) {
    if (item.code === '12') {
      return null;
    }

    const severity = item.severityLevel as 'mild' | 'moderate' | 'severe' | 'profound' | null;
    if (!severity) return null;

    const codeByMode: Record<ServiceModeKey, Partial<Record<'mild' | 'moderate' | 'severe' | 'profound', string>>> = {
      'in-clinic': { mild: 'FJ', moderate: 'FI', severe: 'FH', profound: 'FG' },
      'medical-team': { mild: 'FN', moderate: 'FM', severe: 'FL', profound: 'FK' },
      'home-care': { moderate: 'L4', severe: 'FY', profound: 'FS' },
      'mobile-clinic': { mild: 'L8', moderate: 'L7', severe: 'L6', profound: 'L5' }
    };

    const code = codeByMode[modeKey]?.[severity];
    if (!code) return null;

    const severityLabelMap: Record<'mild' | 'moderate' | 'severe' | 'profound', string> = {
      mild: '輕度',
      moderate: '中度',
      severe: '重度',
      profound: '極重度'
    };

    return {
      code,
      add: this.getAddFromSeverity(severity),
      summary: `非精-${modeKey}-${severityLabelMap[severity]}`,
      manual: false,
      source: 'generic'
    };
  }

  private pickBestMatch(matches: Array<{ code: string; add: number | null }>) {
    if (!matches || matches.length === 0) return null;
    const sorted = [...matches].sort((a, b) => (b.add ?? 0) - (a.add ?? 0));
    return sorted[0];
  }

  /**
   * 只針對符合16案類的項目計算多重障礙評分
   */
  private computeMultipleDisabilityScoreFromItems(
    items: Array<{ item: OldDisabilityCategory; categoryId: string }>
  ): { code: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' } | null {
    if (items.length < 2) {
      return null;
    }

    const selectedItems = items.map(entry => ({
      code: entry.item.code,
      newCategoryId: entry.categoryId,
      severity: entry.item.severityLevel as 'mild' | 'moderate' | 'severe' | 'profound'
    }));

    const uniqueCategories = new Set(selectedItems.map(item => item.newCategoryId));
    if (uniqueCategories.size >= 2) {
      return this.computeMultipleCategoriesSeverity(selectedItems);
    }

    return this.computeSingleCategorySeverity(selectedItems);
  }

  /**
   * 更新符合的障礙別代碼（A-Q）
   * 根據勾選的舊制障礙項和嚴重度，輸出對應的障礙別代碼
   */
  private updateMatchedDisabilityTypeCodes(): void {
    const codes = new Set<string>();

    // 遍歷所有勾選的舊制障礙
    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (item.checked && item.severityLevel && item.newCategoryId !== 'cat-manual') {
          // 09 植物人輕度在居家模式下不輸出障礙別代碼
          if (item.code === '09' && item.severityLevel === 'mild' && this.selectedServiceMode === 'home-care') {
            return;
          }
          // 16 由 manual 項目連動時，不從 disabilityTypeCodes['16'] 輸出 'D'
          // 各 manual 項目（F4/dev-delay/elderly）在下方自行處理各自的障礙別代碼
          if (item.code === '16' && item.linkedByManual) {
            return;
          }
          // 查找該代碼的對應障礙別代碼
          const codeMapping = this.disabilityTypeCodes[item.code];
          if (codeMapping) {
            // 優先查找嚴重度特定的對應，其次查找 'any' 對應
            const disabilityCode = codeMapping[item.severityLevel as any] || codeMapping['any'];
            if (disabilityCode) {
              // 'I' 代碼僅在醫療團模式下才有效，其他模式視為無對應
              if (disabilityCode === 'I' && this.selectedServiceMode !== 'medical-team') {
                return;
              }
              codes.add(disabilityCode);
            }
          }
        }
      });
    });

    // 其他類（cat-manual）的障礙別代碼
    const manualRow = this.disabilityRows.find(r => r.newCategoryId === 'cat-manual');
    if (manualRow) {
      manualRow.items.forEach(item => {
        if (!item.checked) return;

        // 唇顎裂 → D
        if (item.code === 'manual-F4') {
          codes.add('D');
        }
        // 發展遲緩兒童 → N
        if (item.code === 'manual-dev-delay') {
          codes.add('N');
        }
        // 失能老人/長照個案 → O（院內/醫缺巡迴排除）
        if (item.code === 'manual-elderly') {
          if (this.selectedServiceMode !== 'in-clinic' && this.selectedServiceMode !== 'mobile-clinic') {
            codes.add('O');
          }
        }
      });
    }

    // 輸出為字串（使用 ", " 分隔）
    this.matchedDisabilityTypeCodes = Array.from(codes).sort().join(', ');
  }

  /**
   * 將障礙代碼與嚴重度映射到治療項目代號
   */
  private mapDisabilityToTreatmentCode(
    code: string,
    severity: 'mild' | 'moderate' | 'severe' | 'profound',
    modeMapping: any
  ): string | null {
    let mappingKey = '';

    // 判斷類別並映射
    if (code === '12' || code === '06' || code === '13') {
      // 慢性精神病 或 智能障礙 或 多重障礙評分
      if (severity === 'profound' || severity === 'severe') {
        mappingKey = 'psychiatric-severe-above';
      } else if (severity === 'moderate') {
        mappingKey = 'psychiatric-moderate';
      } else if (severity === 'mild') {
        mappingKey = 'non-psychiatric-mild';
      }
    } else if (code === '11' || code === '10') {
      // 自閉症 或 失智症
      mappingKey = 'autism-dementia';
    } else if (code === '05-cerebral-palsy' || code === '05-cerebral' || code === '05-general') {
      // 肢體障礙
      if (severity === 'profound') {
        mappingKey = 'cerebral-limb';
      } else if (severity === 'severe') {
        mappingKey = 'non-psychiatric-severe';
      } else if (severity === 'moderate') {
        mappingKey = 'non-psychiatric-moderate';
      } else if (severity === 'mild') {
        mappingKey = 'non-psychiatric-mild';
      }
    } else {
      // 其他類別，依嚴重程度映射
      if (severity === 'profound') {
        mappingKey = 'non-psychiatric-profound';
      } else if (severity === 'severe') {
        mappingKey = 'non-psychiatric-severe';
      } else if (severity === 'moderate') {
        mappingKey = 'non-psychiatric-moderate';
      } else if (severity === 'mild') {
        mappingKey = 'non-psychiatric-mild';
      }
    }

    // 根據映射取得治療項目代號
    if (mappingKey) {
      const itemsKey = mappingKey as keyof typeof modeMapping.items;
      const treatmentCodes = modeMapping.items[itemsKey];
      if (treatmentCodes && treatmentCodes.length > 0) {
        return treatmentCodes[0]; // 只取第一個代號
      }
    }

    return null;
  }

  /**
   * 處理治療服務模式變更
   */
  onServiceModeChange(): void {
    this.updateMatchedDisabilityTypeCodes();
    this.updateMatchedTreatmentItems();
  }

  /**
   * 清除表單
   */
  clearForm(): void {
    this.memoData = {
      patientNo: this.patientData?.patientNo || '',
      patientName: this.patientData?.patientName || '',
      patientCId: this.patientData?.patientCId || '',
      disabilityCategories: [],
      oldDisabilityCategories: [],
      severityLevels: {},
      memo: '',
      treatmentItems: [],
      specialRequirements: '',
      leftImageUrl: '',
      rightImageUrl: '',
      createdDate: '',
      modifiedDate: ''
    };
    this.leftImagePreview = null;
    this.rightImagePreview = null;
    this.resetTreatmentItems();
    this.matchedTreatmentItem = '';
    this.quickSelectTags = [];
    this.quickSelectInput = '';
    this.newDisabilityCategories.forEach(cat => cat.checked = false);
    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        item.checked = false;
        item.severityLevel = null;
      });
    });
    this.updateServiceModeLockByManualHomeCare();
    // 清除後重新計算代號（為空）
    this.updateMatchedTreatmentItems();
  }

  /**
   * 保存數據
   */
  saveMemo(): void {
    this.isSaving = true;

    // Collect selected items
    this.memoData.disabilityCategories = this.newDisabilityCategories
      .filter(cat => cat.checked)
      .map(cat => cat.id);

    this.memoData.oldDisabilityCategories = [];
    this.memoData.severityLevels = {};

    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (item.checked) {
          this.memoData.oldDisabilityCategories!.push(item.code);
        }
        if (item.severityLevel) {
          this.memoData.severityLevels![item.code] = item.severityLevel;
        }
      });
    });

    this.memoData.treatmentItems = this.treatmentItemsList
      .filter(item => item.checked)
      .map(item => item.value);

    // Set modified date
    this.memoData.modifiedDate = new Date().toISOString();

    // 構建完整的表單數據（包含計算結果）
    const formData: DentalDisabilityFormData = {
      ...this.memoData,
      matchedTreatmentItems: this.matchedTreatmentItem ? [this.matchedTreatmentItem] : [],
      matchedDisabilityTypeCodes: this.matchedDisabilityTypeCodes ? this.matchedDisabilityTypeCodes.split(', ').filter(x => x) : [],
      selectedServiceMode: this.selectedServiceMode,
      quickSelectTags: this.quickSelectTags.map(tag => ({ ...tag })),
      computedMultipleDisabilityScore: this.computeMultipleDisabilityScore()
    };

    // 保存到 service（會同步到 BehaviorSubject 和 sessionStorage）
    this.dentalDisabilityService.updateFormData(formData);

    // ===== 轉換為優化的 JSON 格式 =====
    const disabilityScoresStr = this.buildDisabilityScores();
    const computedScoreStr = this.formatComputedScore();
    const disabilityCategoriesStr = (this.memoData.disabilityCategories || []).join(', ');
    const specialRequirementsStr = (this.memoData.treatmentItems || []).join(', ');
    const matchedTreatmentItemStr = this.matchedTreatmentItem || '';
    const matchedDisabilityTypeCodesStr = this.matchedDisabilityTypeCodes || '';

    const formDataOptimized: DentalDisabilityFormDataOptimized = {
      patientNo: this.memoData.patientNo,
      patientName: this.memoData.patientName,
      patientCId: this.memoData.patientCId,
      disabilityCategories: disabilityCategoriesStr,
      disabilityScores: disabilityScoresStr,
      specialRequirements: specialRequirementsStr,
      leftImageUrl: this.memoData.leftImageUrl,
      rightImageUrl: this.memoData.rightImageUrl,
      modifiedDate: this.memoData.modifiedDate,
      matchedTreatmentItem: matchedTreatmentItemStr,
      matchedDisabilityTypeCodes: matchedDisabilityTypeCodesStr,
      selectedServiceMode: this.selectedServiceMode,
      computedMultipleDisabilityScore: computedScoreStr
    };

    // 保存優化格式到 service
    this.dentalDisabilityService.updateFormDataOptimized(formDataOptimized);

    // ===== DEBUG: 輸出優化後的 JSON 格式 =====
    console.log('='.repeat(60));
    console.log('📋 身障註記表單數據已保存（優化格式）');
    console.log('='.repeat(60));
    console.log('優化後的 JSON 數據:');
    console.log(formDataOptimized);
    console.log('='.repeat(60));

    // Emit result - 傳送優化格式給父組件
    setTimeout(() => {
      this.resultEmit.emit(formDataOptimized);
      this.isSaving = false;
      this.isEditMode = false;
    }, 300);
  }

  /**
   * 構建 disabilityScores 字串 (格式: "06.1, 09.2, 10.3, ...")
   */
  private buildDisabilityScores(): string {
    const scores: string[] = [];
    const severityMap: { [key: string]: string } = {
      'mild': '1',
      'moderate': '2',
      'severe': '3',
      'profound': '4'
    };

    this.disabilityRows.forEach(row => {
      row.items.forEach(item => {
        if (item.checked && item.severityLevel && item.newCategoryId !== 'cat-manual') {
          const severityDigit = severityMap[item.severityLevel];
          if (severityDigit) {
            scores.push(`${item.code}.${severityDigit}`);
          }
        }
      });
    });

    return Array.from(new Set(scores)).join(', ');
  }

  /**
   * 格式化 computedMultipleDisabilityScore (格式: "13.4")
   */
  private formatComputedScore(): string | null {
    const score = this.computeMultipleDisabilityScore();
    if (!score) return null;

    const severityMap: { [key: string]: string } = {
      'mild': '1',
      'moderate': '2',
      'severe': '3',
      'profound': '4'
    };

    const severityDigit = severityMap[score.severity || ''] || '';
    if (severityDigit) {
      return `${score.code}.${severityDigit}`;
    }
    return null;
  }

  /**
   * 關閉對話框
   */
  closeDialog(): void {
    this.closeEmit.emit();
  }

  /**
   * 取消編輯
   */
  async cancelEdit(): Promise<void> {
    // 顯示確認視窗：是否放棄本次編輯內容？
    const ret = await this.userConfirm.showConfirm({
      title: '提示',
      msg: '是否放棄本次編輯內容？',
      textYes: '放棄',
      textNo: '繼續編輯',
      width: 350
    });

    if (ret) {
      // 放棄：回復快照並離開編輯模式
      this.restoreSnapshot();
      this.isEditMode = false;
      // 清除快照
      this.editSnapshot = null;
    } else {
      // 繼續編輯：不動作
    }
  }

  /**
   * 建立目前填答的快照
   */
  private captureSnapshot(): void {
    this.editSnapshot = {
      memoData: JSON.parse(JSON.stringify(this.memoData)),
      leftImagePreview: this.leftImagePreview,
      rightImagePreview: this.rightImagePreview,
      selectedServiceMode: this.selectedServiceMode,
      newDisabilityCategories: this.newDisabilityCategories.map(cat => ({ id: cat.id, checked: cat.checked })),
      disabilityRows: this.disabilityRows.map(row => ({
        newCategoryId: row.newCategoryId,
        items: row.items.map(item => ({ code: item.code, checked: item.checked, severityLevel: item.severityLevel }))
      })),
      treatmentItemsChecked: this.treatmentItemsList.map(item => item.checked),
      patientRemark: this.histRcd?.Patient?.Remark || '',
      quickSelectTags: this.quickSelectTags.map(t => ({ ...t })),
      quickSelectInput: this.quickSelectInput,
    };
  }

  /**
   * 回復快照內容
   */
  private restoreSnapshot(): void {
    if (!this.editSnapshot) return;

    // 基本資料
    this.memoData = JSON.parse(JSON.stringify(this.editSnapshot.memoData));
    this.leftImagePreview = this.editSnapshot.leftImagePreview;
    this.rightImagePreview = this.editSnapshot.rightImagePreview;
    this.selectedServiceMode = this.editSnapshot.selectedServiceMode;

    // 新制類別
    const catsSnap = this.editSnapshot.newDisabilityCategories as Array<{ id: string; checked: boolean }>;
    this.newDisabilityCategories.forEach(cat => {
      const snap = catsSnap.find(s => s.id === cat.id);
      cat.checked = !!snap && snap.checked;
    });

    // 舊制類別與嚴重程度
    const rowsSnap = this.editSnapshot.disabilityRows as Array<{ newCategoryId: string; items: Array<{ code: string; checked: boolean; severityLevel: any }> }>;
    this.disabilityRows.forEach(row => {
      const rowSnap = rowsSnap.find(r => r.newCategoryId === row.newCategoryId);
      if (rowSnap) {
        row.items.forEach(item => {
          const itemSnap = rowSnap.items.find(i => i.code === item.code);
          if (itemSnap) {
            item.checked = itemSnap.checked;
            item.severityLevel = itemSnap.severityLevel as any;
          }
        });
      }
    });

    // 治療特殊需求
    const tSnap = this.editSnapshot.treatmentItemsChecked as boolean[];
    this.treatmentItemsList.forEach((item, idx) => {
      item.checked = tSnap[idx] || false;
    });

    // 綜合治療註記
    if (this.histRcd && this.histRcd.Patient) {
      this.histRcd.Patient.Remark = this.editSnapshot.patientRemark || '';
    }

    // 快選標籤與輸入
    this.quickSelectTags = (this.editSnapshot.quickSelectTags as any[])?.map(t => ({ ...t })) || [];
    this.quickSelectInput = this.editSnapshot.quickSelectInput || '';
    if (this.quickSelectBox && this.quickSelectBox.nativeElement) {
      this.quickSelectBox.nativeElement.innerText = this.quickSelectInput;
    }

    this.updateServiceModeLockByManualHomeCare();

    // 重新計算符合的特殊治療代號
    this.updateMatchedTreatmentItems();
  }

  /**
   * 處理圖片拖曳進入
   */
  onDragOver(event: DragEvent, side: 'left' | 'right'): void {
    event.preventDefault();
    event.stopPropagation();
    if (side === 'left') {
      this.isDraggingLeft = true;
    } else {
      this.isDraggingRight = true;
    }
  }

  /**
   * 處理圖片拖曳離開
   */
  onDragLeave(event: DragEvent, side: 'left' | 'right'): void {
    event.preventDefault();
    event.stopPropagation();
    if (side === 'left') {
      this.isDraggingLeft = false;
    } else {
      this.isDraggingRight = false;
    }
  }

  /**
   * 處理圖片拖放
   */
  onDrop(event: DragEvent, side: 'left' | 'right'): void {
    event.preventDefault();
    event.stopPropagation();

    if (side === 'left') {
      this.isDraggingLeft = false;
    } else {
      this.isDraggingRight = false;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleImageFile(files[0], side);
    }
  }

  /**
   * 處理圖片點擊上傳
   */
  onImageClick(side: 'left' | 'right'): void {
    if (!this.isEditMode) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        this.handleImageFile(file, side);
      }
    };
    input.click();
  }

  /**
   * 處理圖片檔案
   */
  private handleImageFile(file: File, side: 'left' | 'right'): void {
    if (!file.type.startsWith('image/')) {
      alert('請選擇圖片檔案');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const imageUrl = e.target.result;
      if (side === 'left') {
        this.leftImagePreview = imageUrl;
        this.memoData.leftImageUrl = imageUrl;
      } else {
        this.rightImagePreview = imageUrl;
        this.memoData.rightImageUrl = imageUrl;
      }
    };
    reader.readAsDataURL(file);
  }

  /**
   * 移除圖片
   */
  removeImage(side: 'left' | 'right', event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (side === 'left') {
      this.leftImagePreview = null;
      this.memoData.leftImageUrl = '';
    } else {
      this.rightImagePreview = null;
      this.memoData.rightImageUrl = '';
    }
  }
}
